import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Gift, Pencil, Plus } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { referralsService, type PartnerPromotion, type PartnerPromotionPayload } from '@/services/referralsService'
import { useAuthStore } from '@/stores/authStore'
import { PromotionParticipantsSection, PromotionVigenteView } from '@/pages/promocoes/PromotionVigenteView'

type FormState = {
  id?: string
  title: string
  rewardName: string
  description: string
  bannerText: string
  imageUrl: string
  targetValue: string
  startDate: string
  endDate: string
  isActive: boolean
  partnershipType: string
}

const emptyForm: FormState = {
  title: '',
  rewardName: '',
  description: '',
  bannerText: '',
  imageUrl: '',
  targetValue: '8000',
  startDate: '',
  endDate: '',
  isActive: false,
  partnershipType: '',
}

function toInputDate(value: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

function fromPromotion(promotion: PartnerPromotion): FormState {
  return {
    id: promotion.id,
    title: promotion.title,
    rewardName: promotion.rewardName,
    description: promotion.description,
    bannerText: promotion.bannerText ?? '',
    imageUrl: promotion.imageUrl ?? '',
    targetValue: String(promotion.targetValue),
    startDate: toInputDate(promotion.startDate),
    endDate: toInputDate(promotion.endDate),
    isActive: promotion.isActive,
    partnershipType: promotion.partnershipType ?? '',
  }
}

function toPayload(form: FormState): PartnerPromotionPayload {
  return {
    id: form.id,
    title: form.title,
    rewardName: form.rewardName,
    description: form.description,
    bannerText: form.bannerText || null,
    imageUrl: form.imageUrl || null,
    targetValue: Number(form.targetValue),
    startDate: form.startDate,
    endDate: form.endDate,
    isActive: form.isActive,
    partnershipType: form.partnershipType || null,
  }
}

function money(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export default function PartnerPromotions() {
  const role = useAuthStore((state) => state.user?.role)
  if (role !== 'admin') return <PromotionVigenteView />

  return <PromotionEditor />
}

function PromotionEditor() {
  const [items, setItems] = useState<PartnerPromotion[]>([])
  const [form, setForm] = useState<FormState>(emptyForm)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)

  const active = useMemo(() => items.find((item) => item.isActive), [items])

  async function load() {
    setLoading(true)
    try {
      setItems(await referralsService.listPromotions())
    } catch {
      toast.error('Não foi possível carregar as promoções.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  function patch<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function save() {
    setSaving(true)
    try {
      await referralsService.savePromotion(toPayload(form))
      toast.success(form.id ? 'Promoção atualizada.' : 'Promoção criada.')
      setForm(emptyForm)
      await load()
    } catch {
      toast.error('Não foi possível salvar a promoção.')
    } finally {
      setSaving(false)
    }
  }

  async function toggle(promotion: PartnerPromotion) {
    try {
      await referralsService.savePromotion({ ...promotion, isActive: !promotion.isActive })
      toast.success(promotion.isActive ? 'Promoção desativada.' : 'Promoção ativada.')
      await load()
    } catch {
      toast.error('Não foi possível alterar o status.')
    }
  }

  async function uploadBanner(file?: File) {
    if (!file) return
    setUploading(true)
    try {
      const data = await referralsService.uploadPromotionBanner(file)
      patch('imageUrl', data.imageUrl)
      toast.success('Banner enviado.')
    } catch {
      toast.error('Não foi possível enviar o banner.')
    } finally {
      setUploading(false)
    }
  }

  const disabled = saving || !form.title || !form.rewardName || !form.description || !form.startDate || !form.endDate

  return (
    <DashboardLayout title='Promoções'>
    <div className='pb-20 md:pb-6'>
      <div className='mb-5 flex flex-col gap-3 md:flex-row md:items-end md:justify-between'>
        <div>
          <p className='text-xs font-semibold uppercase tracking-[0.2em] text-text-muted'>Parceiros</p>
          <h1 className='mt-1 text-2xl font-bold text-text-primary'>Promoções</h1>
          <p className='mt-2 max-w-2xl text-sm text-text-muted'>
            Gerencie o banner, texto, período e régua de valor exibidos no Portal do Parceiro.
          </p>
        </div>
        {active && (
          <div className='glass-card px-4 py-3 text-sm'>
            <p className='text-text-muted'>Ativa agora</p>
            <p className='font-semibold text-text-primary'>{active.rewardName}</p>
          </div>
        )}
      </div>

      <div className='grid gap-4 xl:grid-cols-[420px_1fr]'>
        <section className='glass-card'>
          <div className='mb-4 flex items-center justify-between'>
            <h2 className='text-base font-semibold text-text-primary'>
              {form.id ? 'Editar promoção' : 'Nova promoção'}
            </h2>
            {form.id && (
              <button className='text-xs text-text-muted hover:text-text-primary' onClick={() => setForm(emptyForm)}>
                Limpar
              </button>
            )}
          </div>

          <div className='space-y-3'>
            <input className='premium-input w-full' placeholder='Título do banner' value={form.title} onChange={(e) => patch('title', e.target.value)} />
            <input className='premium-input w-full' placeholder='Prêmio / chamada principal' value={form.rewardName} onChange={(e) => patch('rewardName', e.target.value)} />
            <textarea className='premium-input min-h-[96px] w-full' placeholder='Descrição da campanha' value={form.description} onChange={(e) => patch('description', e.target.value)} />
            <textarea className='premium-input min-h-[80px] w-full' placeholder='Texto curto do banner no portal' value={form.bannerText} onChange={(e) => patch('bannerText', e.target.value)} />
            <div className='rounded-2xl border border-[var(--glass-border)] bg-white/[0.03] p-3'>
              <label className='text-xs font-semibold uppercase tracking-wide text-text-muted'>Banner da promoção</label>
              {form.imageUrl && (
                <img src={form.imageUrl} alt='Prévia do banner' className='mt-3 max-h-40 w-full rounded-xl bg-white object-contain' />
              )}
              <div className='mt-3 grid gap-2'>
                <input className='premium-input w-full' placeholder='URL da imagem/banner' value={form.imageUrl} onChange={(e) => patch('imageUrl', e.target.value)} />
                <input
                  className='block w-full text-sm text-text-muted file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--brand-primary)] file:px-3 file:py-2 file:text-sm file:font-semibold file:text-white'
                  type='file'
                  accept='image/png,image/jpeg,image/webp,image/gif'
                  disabled={uploading}
                  onChange={(e) => void uploadBanner(e.target.files?.[0])}
                />
                <p className='text-xs text-text-muted'>
                  {uploading ? 'Enviando banner...' : 'Você pode subir uma imagem ou informar uma URL externa.'}
                </p>
              </div>
            </div>
            <div className='grid gap-3 sm:grid-cols-2'>
              <input className='premium-input w-full' type='date' value={form.startDate} onChange={(e) => patch('startDate', e.target.value)} />
              <input className='premium-input w-full' type='date' value={form.endDate} onChange={(e) => patch('endDate', e.target.value)} />
            </div>
            <div className='grid gap-3 sm:grid-cols-2'>
              <input className='premium-input w-full' type='number' min='1' step='100' placeholder='Meta da régua' value={form.targetValue} onChange={(e) => patch('targetValue', e.target.value)} />
              <input className='premium-input w-full' placeholder='Tipo de parceria' value={form.partnershipType} onChange={(e) => patch('partnershipType', e.target.value)} />
            </div>
            <label className='flex items-center gap-2 text-sm text-text-muted'>
              <input type='checkbox' checked={form.isActive} onChange={(e) => patch('isActive', e.target.checked)} />
              Ativar no Portal do Parceiro
            </label>
            <button
              className='flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--brand-primary)] px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60'
              disabled={disabled}
              onClick={save}
            >
              <Plus size={16} />
              {saving ? 'Salvando...' : 'Salvar promoção'}
            </button>
          </div>
        </section>

        <section className='glass-card overflow-hidden'>
          <div className='mb-4 flex items-center justify-between'>
            <h2 className='text-base font-semibold text-text-primary'>Histórico</h2>
            <span className='text-xs text-text-muted'>{loading ? 'Carregando...' : `${items.length} promoções`}</span>
          </div>
          <div className='grid gap-3'>
            {items.map((item) => (
              <article key={item.id} className='rounded-2xl border border-[var(--glass-border)] bg-white/[0.03] p-4'>
                <div className='flex flex-col gap-3 md:flex-row md:items-start md:justify-between'>
                  <div className='min-w-0'>
                    <p className='flex items-center gap-2 text-sm font-semibold text-text-primary'>
                      <Gift size={16} />
                      {item.rewardName}
                    </p>
                    <p className='mt-1 text-xs uppercase tracking-wide text-text-muted'>{item.title}</p>
                    <p className='mt-2 line-clamp-2 text-sm text-text-muted'>{item.bannerText || item.description}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${item.isActive ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/5 text-text-muted'}`}>
                    {item.isActive ? 'Ativa' : 'Inativa'}
                  </span>
                </div>
                <div className='mt-4 flex flex-wrap items-center gap-2 text-xs text-text-muted'>
                  <span>{toInputDate(item.startDate)} a {toInputDate(item.endDate)}</span>
                  <span>Meta {money(item.targetValue)}</span>
                  {item.partnershipType && <span>{item.partnershipType}</span>}
                </div>
                <div className='mt-4 flex flex-wrap gap-2'>
                  <button className='rounded-lg border border-[var(--glass-border)] px-3 py-2 text-xs text-text-primary hover:bg-white/[0.05]' onClick={() => setForm(fromPromotion(item))}>
                    <Pencil size={14} className='mr-1 inline' />
                    Editar
                  </button>
                  <button className='rounded-lg border border-[var(--glass-border)] px-3 py-2 text-xs text-text-primary hover:bg-white/[0.05]' onClick={() => toggle(item)}>
                    {item.isActive ? 'Desativar' : 'Ativar'}
                  </button>
                </div>
              </article>
            ))}
            {!loading && items.length === 0 && (
              <p className='rounded-2xl border border-dashed border-[var(--glass-border)] p-6 text-sm text-text-muted'>
                Nenhuma promoção cadastrada ainda.
              </p>
            )}
          </div>
        </section>
      </div>
      <PromotionParticipantsSection />
    </div>
    </DashboardLayout>
  )
}
